// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import { create } from 'zustand';
import { normalizeGameState, buildInitialState, mergeGameState } from './gameStateHelpers';
import { logger } from '../lib/logger';
import type { GameState, LobbyState, LobbySettings } from '../types/game';

export interface GameStateStore {
  gameState: GameState | LobbyState | null;
  lastSeenSequence: number;
  lobbySettings: LobbySettings | null;

  updateGameState: (
    state: GameState | LobbyState,
    currentPlayerId: string | null,
    sequence?: number
  ) => void;
  updateSequence: (sequence: number) => void;
  getLastSeenSequence: () => number;
  clearGameState: () => void;
  updateLobbySettings: (
    settings: LobbySettings,
    changes?: Record<string, { old: string | number | boolean; new: string | number | boolean }>
  ) => void;
  isInLobby: () => boolean;
  isInGame: () => boolean;
}

export const useGameStateStore = create<GameStateStore>()((set, get) => ({
  gameState: null,
  lastSeenSequence: 0,
  lobbySettings: null,

  updateGameState: (state, currentPlayerId, sequence) => {
    if (sequence !== undefined && sequence > get().lastSeenSequence) {
      set({ lastSeenSequence: sequence });
    }

    const normalized = normalizeGameState(state);
    logger.debug('updateGameState called', { incoming: normalized });

    set(current => {
      const previous = current.gameState;
      logger.debug('updateGameState inside set', { previous });

      if (!previous) {
        const initial = buildInitialState(normalized);
        logger.debug('updateGameState setting initial state', { initial });
        return { gameState: initial };
      }

      const merged = mergeGameState(previous, normalized, currentPlayerId);

      const incomingRec = normalized as unknown as Record<string, unknown>;
      const prevRec = previous as unknown as Record<string, unknown>;
      const mergedRec = merged as unknown as Record<string, unknown>;

      if ('is_my_turn' in incomingRec || 'is_my_turn' in prevRec) {
        logger.info('is_my_turn_tracking', {
          incoming: incomingRec.is_my_turn,
          previous: prevRec.is_my_turn,
          merged: mergedRec.is_my_turn,
          inIncoming: 'is_my_turn' in incomingRec,
          inPrevious: 'is_my_turn' in prevRec,
        });
      }

      if ('player_phase' in incomingRec || 'player_phase' in prevRec) {
        logger.info('player_phase_tracking', {
          incoming: incomingRec.player_phase,
          previous: prevRec.player_phase,
          merged: mergedRec.player_phase,
          inIncoming: 'player_phase' in incomingRec,
          inPrevious: 'player_phase' in prevRec,
          disputeInIncoming: 'dispute' in incomingRec,
          disputeInPrevious: 'dispute' in prevRec,
        });
      }

      logger.debug('updateGameState setting merged state', { merged });
      return { gameState: merged };
    });
    logger.debug('game_state_updated', { gameState: get().gameState });
  },

  updateSequence: (sequence: number) => {
    if (sequence > get().lastSeenSequence) {
      set({ lastSeenSequence: sequence });
    }
  },

  getLastSeenSequence: () => get().lastSeenSequence,

  clearGameState: () => {
    set({
      gameState: null,
      lastSeenSequence: 0,
      lobbySettings: null,
    });
  },

  updateLobbySettings: (settings, changes) => {
    logger.info('lobby_settings_updated', { settings, changes });
    set(current => {
      const lobbyGameState =
        current.gameState?.room_status === 'lobby'
          ? (current.gameState as import('../types/game').LobbyState)
          : null;
      const existingLobbySettings =
        current.lobbySettings ?? lobbyGameState?.lobby_settings ?? {};
      const mergedSettings: LobbySettings = {
        ...existingLobbySettings,
        ...settings,
      } as LobbySettings;
      const nextState: Partial<GameStateStore> = { lobbySettings: mergedSettings };

      if (current.gameState?.room_status === 'lobby') {
        const lobbyState = { ...current.gameState, lobby_settings: mergedSettings } as LobbyState;

        if (typeof mergedSettings.min_players === 'number') {
          lobbyState.min_players = mergedSettings.min_players;
        }

        if (typeof mergedSettings.max_players === 'number') {
          lobbyState.max_players = mergedSettings.max_players;
        }

        if (typeof mergedSettings.allow_spectators === 'boolean') {
          lobbyState.allow_spectators = mergedSettings.allow_spectators;
        }

        nextState.gameState = lobbyState;
      }

      return nextState;
    });
  },

  isInLobby: () => {
    const state = get().gameState;
    return state?.room_status === 'lobby';
  },

  isInGame: () => {
    const state = get().gameState;
    return state !== null && state.room_status !== 'lobby';
  },
}));
